import { useState, useEffect, useCallback, useRef } from 'react';

let wasmInstancePromise = null;

// Stable filenames are cached for a year on deploy. The glue script and the
// wasm binary must come from the same build, or instantiation fails with
// "Import #0 module is not an object".
const WASM_BUILD_ID = process.env.WASM_BUILD_ID || '';

function wasmAssetUrl(fileName) {
  const base = `/${fileName}`;
  return WASM_BUILD_ID ? `${base}?v=${WASM_BUILD_ID}` : base;
}

function getWasmInstance() {
  if (wasmInstancePromise) return wasmInstancePromise;

  wasmInstancePromise = (async () => {
    if (typeof window.audio_module === 'undefined') {
      const scriptUrl = wasmAssetUrl('index.js');
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.onload = resolve;
        script.onerror = () => reject(
          new Error(`Failed to load WASM script (${scriptUrl})`)
        );
        document.head.appendChild(script);
      });
    }

    if (typeof window.audio_module === 'undefined') {
      throw new Error('WASM module (audio_module) not found after loading script.');
    }

    const wasmInstance = await window.audio_module({
      locateFile: (path) => wasmAssetUrl(path),
    });
    if (wasmInstance._initialize) {
      wasmInstance._initialize();
    }

    if (wasmInstance._set_reverb_preset) {
      wasmInstance._set_reverb_preset(0.5, 1.8, 0.3, 0.6, 10.0, 0.4);
    }

    const defaultDistance = 0.5;
    const defaultSpeakerGain = 40;
    const defaultMicGain = 50;
    if (wasmInstance._set_feedback_from_reverb) {
      wasmInstance._set_feedback_from_reverb(
        defaultDistance,
        defaultSpeakerGain,
        defaultMicGain
      );
    }

    if (wasmInstance._get_output_feedback) {
      console.log(
        'Audio feedback initialized:',
        wasmInstance._get_output_feedback()
      );
    }

    return wasmInstance;
  })().catch((loadError) => {
    wasmInstancePromise = null;
    throw loadError;
  });

  return wasmInstancePromise;
}

const ZPLANE_VALUES_PER_STAGE = 6;
const ZPLANE_MAX_STAGES = 32;

/**
 * Custom hook to load and manage the WASM audio module lifecycle
 * @returns {Object} { instance, isLoading, error, isReady }
 */
export function useWasm() {
  const [instance, setInstance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function loadWasm() {
      try {
        setIsLoading(true);
        setError(null);
        const wasmInstance = await getWasmInstance();

        if (isMounted) {
          instanceRef.current = wasmInstance;
          setInstance(wasmInstance);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to load WASM module:', err);
        if (isMounted) {
          setError(err);
          setIsLoading(false);
        }
      }
    }

    loadWasm();

    return () => {
      isMounted = false;
    };
  }, []);

  // WASM function wrappers
  const getAudioBuffer = useCallback(() => {
    if (!instanceRef.current) return null;
    const bufferPointer = instanceRef.current._generate_audio_buffer();
    const bufferSize = instanceRef.current._get_buffer_size();
    return new Float32Array(
      instanceRef.current.HEAPF32.buffer,
      bufferPointer,
      bufferSize
    );
  }, []);

  const setDelayTime = useCallback((time) => {
    if (instanceRef.current?._set_delay_time) {
      instanceRef.current._set_delay_time(time);
    }
  }, []);

  const setDelayFeedback = useCallback((feedback) => {
    if (instanceRef.current?._set_delay_feedback) {
      instanceRef.current._set_delay_feedback(feedback);
    }
  }, []);

  const setReverbPreset = useCallback((roomSize, rt60, damping, diffusion, preDelayMs, wet) => {
    if (instanceRef.current?._set_reverb_preset) {
      instanceRef.current._set_reverb_preset(roomSize, rt60, damping, diffusion, preDelayMs, wet);
    }
  }, []);

  const setFeedbackFromReverb = useCallback((distance, speakerGain, micGain) => {
    if (instanceRef.current?._set_feedback_from_reverb) {
      instanceRef.current._set_feedback_from_reverb(distance, speakerGain, micGain);
    }
  }, []);

  const getOutputFeedback = useCallback(() => {
    if (instanceRef.current?._get_output_feedback) {
      return instanceRef.current._get_output_feedback();
    }
    return 0;
  }, []);

  const setOutputFeedback = useCallback((feedback) => {
    if (instanceRef.current?._set_output_feedback) {
      instanceRef.current._set_output_feedback(feedback);
    }
  }, []);

  const setScenarioEnabled = useCallback((enabled) => {
    instanceRef.current?._set_scenario_enabled?.(enabled ? 1 : 0);
  }, []);

  const setScenarioTransducers = useCallback((micProfile, speakerProfile) => {
    instanceRef.current?._set_scenario_transducers?.(micProfile, speakerProfile);
  }, []);

  const setScenarioGeometry = useCallback((micMonitorDistance, sourceMicDistance, angleDegrees) => {
    instanceRef.current?._set_scenario_geometry?.(
      micMonitorDistance,
      sourceMicDistance,
      angleDegrees
    );
  }, []);

  const setScenarioRoom = useCallback((length, width, height, surfacePreset, occupancy) => {
    instanceRef.current?._set_scenario_room?.(
      length,
      width,
      height,
      surfacePreset,
      occupancy
    );
  }, []);

  const setScenarioAtmosphere = useCallback((temperatureC, humidityPercent, pressureKpa) => {
    instanceRef.current?._set_scenario_atmosphere?.(
      temperatureC,
      humidityPercent,
      pressureKpa
    );
  }, []);

  const setScenarioConsoleLatency = useCallback((latencyMs) => {
    instanceRef.current?._set_scenario_console_latency?.(latencyMs);
  }, []);

  const setScenarioMonitorGain = useCallback((gainDb) => {
    instanceRef.current?._set_scenario_monitor_gain?.(gainDb);
  }, []);

  const setEqCut = useCallback((bandIndex, gainDb) => {
    instanceRef.current?._set_eq_cut?.(bandIndex, gainDb);
  }, []);

  const getEqCut = useCallback((bandIndex) => (
    instanceRef.current?._get_eq_cut?.(bandIndex) ?? 0
  ), []);

  const resetEqCuts = useCallback(() => {
    instanceRef.current?._reset_eq_cuts?.();
  }, []);

  const analyzeEqGiveback = useCallback((thresholdDb, ringLimitDb, stepDb) => {
    const wasm = instanceRef.current;
    if (!wasm?._analyze_eq_giveback || !wasm._get_eq_giveback_size) {
      return null;
    }
    const pointer = wasm._analyze_eq_giveback(thresholdDb, ringLimitDb, stepDb);
    const size = wasm._get_eq_giveback_size();
    return Float32Array.from(new Float32Array(
      wasm.HEAPF32.buffer,
      pointer,
      size
    ));
  }, []);

  const setChannelHighpass = useCallback((enabled) => {
    instanceRef.current?._set_channel_hpf?.(enabled ? 1 : 0);
  }, []);

  const getChannelHighpass = useCallback(() => (
    (instanceRef.current?._get_channel_hpf?.() ?? 0) !== 0
  ), []);

  const getOpenLoopResponse = useCallback(() => {
    const wasm = instanceRef.current;
    if (
      !wasm?._generate_open_loop_response ||
      !wasm._get_open_loop_response_frequencies ||
      !wasm._get_open_loop_response_size
    ) {
      return null;
    }
    const magnitudesPointer = wasm._generate_open_loop_response();
    const frequenciesPointer = wasm._get_open_loop_response_frequencies();
    const size = wasm._get_open_loop_response_size();
    return {
      frequencies: Float32Array.from(new Float32Array(
        wasm.HEAPF32.buffer,
        frequenciesPointer,
        size
      )),
      magnitudesDb: Float32Array.from(new Float32Array(
        wasm.HEAPF32.buffer,
        magnitudesPointer,
        size
      )),
      hottestFrequency: wasm._get_hottest_frequency?.() ?? 0,
      stabilityMarginDb: wasm._get_stability_margin_db?.() ?? -80,
    };
  }, []);

  const resetFeedbackBuffer = useCallback(() => {
    if (instanceRef.current?._reset_feedback_buffer) {
      instanceRef.current._reset_feedback_buffer();
    }
  }, []);

  const resetFadeIn = useCallback(() => {
    if (instanceRef.current?._reset_fade_in) {
      instanceRef.current._reset_fade_in();
    }
  }, []);

  const setAudioSourceMode = useCallback((mode) => {
    if (instanceRef.current?._set_audio_source_mode) {
      instanceRef.current._set_audio_source_mode(mode);
    }
  }, []);

  const setTargetFrequencyRange = useCallback((min, max) => {
    if (instanceRef.current?._set_target_frequency_range) {
      instanceRef.current._set_target_frequency_range(min, max);
      const appliedMin = instanceRef.current._get_target_frequency_min
        ? instanceRef.current._get_target_frequency_min()
        : 0;
      const appliedMax = instanceRef.current._get_target_frequency_max
        ? instanceRef.current._get_target_frequency_max()
        : 0;
      if ((appliedMin > 0 || appliedMax > 0) && instanceRef.current._reset_feedback_buffer) {
        instanceRef.current._reset_feedback_buffer();
      }
    }
  }, []);

  const getTargetFrequencyMin = useCallback(() => {
    if (instanceRef.current?._get_target_frequency_min) {
      return instanceRef.current._get_target_frequency_min();
    }
    return 0;
  }, []);

  const getTargetFrequencyMax = useCallback(() => {
    if (instanceRef.current?._get_target_frequency_max) {
      return instanceRef.current._get_target_frequency_max();
    }
    return 0;
  }, []);

  const getZPlaneStages = useCallback(() => {
    const wasm = instanceRef.current;
    if (!wasm?._fill_zplane_stages || !wasm._get_zplane_stage_buffer) {
      return [];
    }

    const stageCount = Math.min(
      ZPLANE_MAX_STAGES,
      Math.max(0, wasm._fill_zplane_stages())
    );
    if (stageCount === 0) {
      return [];
    }

    const bufferPointer = wasm._get_zplane_stage_buffer();
    if (!bufferPointer) {
      return [];
    }

    const packedStages = new Float32Array(
      wasm.HEAPF32.buffer,
      bufferPointer,
      stageCount * ZPLANE_VALUES_PER_STAGE
    );

    return Array.from({ length: stageCount }, (_, index) => {
      const offset = index * ZPLANE_VALUES_PER_STAGE;
      return {
        type: Math.round(packedStages[offset]),
        b0: packedStages[offset + 1],
        b1: packedStages[offset + 2],
        b2: packedStages[offset + 3],
        a1: packedStages[offset + 4],
        a2: packedStages[offset + 5],
      };
    });
  }, []);

  return {
    instance,
    isLoading,
    error,
    isReady: !isLoading && !error && instance !== null,
    // WASM function wrappers
    getAudioBuffer,
    setDelayTime,
    setDelayFeedback,
    setReverbPreset,
    setFeedbackFromReverb,
    getOutputFeedback,
    setOutputFeedback,
    setScenarioEnabled,
    setScenarioTransducers,
    setScenarioGeometry,
    setScenarioRoom,
    setScenarioAtmosphere,
    setScenarioConsoleLatency,
    setScenarioMonitorGain,
    setEqCut,
    getEqCut,
    resetEqCuts,
    analyzeEqGiveback,
    setChannelHighpass,
    getChannelHighpass,
    getOpenLoopResponse,
    resetFeedbackBuffer,
    resetFadeIn,
    setAudioSourceMode,
    setTargetFrequencyRange,
    getTargetFrequencyMin,
    getTargetFrequencyMax,
    getZPlaneStages,
  };
}

export default useWasm;

